import { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lookupTechnique } from '../../lib/graphApi';
import type { TechniqueLookupResult } from '../../lib/graphApi';
import {
  actorDetailPath,
  familyDetailPath,
  techniqueDetailPath,
  iocDetailPath,
} from '../../lib/paths';
import EntityDetailShell from '../entity/EntityDetailShell';
import EntityHeader, { type HeaderAction } from '../entity/EntityHeader';
import FactsBand, { type FactsStat } from '../entity/FactsBand';
import DocumentsTable, { type DocumentRow } from '../entity/DocumentsTable';
import CoOccurrenceTable, { type CoOccurrenceRow } from '../entity/CoOccurrenceTable';
import { sparklineFromDocs } from '../entity/sparklineFromDocs';
import { ENTITY_COLORS } from '../../lib/entityColors';

function mitreUrl(attackId: string): string {
  return `https://attack.mitre.org/techniques/${attackId.replace('.', '/')}`;
}

export default function TechniqueDetailPage() {
  const params = useParams();
  const navigate = useNavigate();

  const attackId = params.attackId ? decodeURIComponent(params.attackId) : '';

  const { data, isLoading } = useQuery<TechniqueLookupResult>({
    queryKey: ['technique-detail', attackId],
    queryFn: () => lookupTechnique(attackId),
    enabled: !!attackId,
  });

  const stats = data?.stats;
  const tactics = data?.tactics || [];
  const actors = data?.actors || [];
  const families = data?.families || [];
  const cves = data?.cves || [];
  const subtechniques = data?.subtechniques || [];
  const documents: DocumentRow[] = useMemo(() => (data?.documents || []).map((d) => ({ ...d })), [data?.documents]);

  const displayId = data?.attack_id || attackId;
  const displayName = data?.name || '';
  const graphUrl = `/graph?seed=${encodeURIComponent(displayId)}&label=AttackTechnique`;

  const coRows: CoOccurrenceRow[] = useMemo(() => {
    const rows: CoOccurrenceRow[] = [];
    for (const a of actors) {
      rows.push({
        id: `actor::${a.actor_id || a.common_name}`,
        type: 'Actor',
        value: a.common_name || a.actor_id,
        href: actorDetailPath(a.common_name || a.actor_id),
        count: 1,
      });
    }
    for (const f of families) {
      rows.push({
        id: `family::${f.family_id || f.common_name}`,
        type: 'Family',
        value: f.common_name || f.family_id,
        href: familyDetailPath(f.common_name || f.family_id),
        count: 1,
      });
    }
    for (const c of cves) {
      rows.push({
        id: `cve::${c.value}`,
        type: 'CVE',
        value: c.value,
        href: iocDetailPath(c.value, 'cve'),
        count: c.cvss_score != null ? Math.max(1, Math.round(c.cvss_score)) : 1,
      });
    }
    return rows;
  }, [actors, families, cves]);

  if (isLoading) {
    return (
      <div className="ioc-detail-page">
        <button className="ioc-detail-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <div className="ioc-detail-loading">Loading technique</div>
      </div>
    );
  }

  const actions: HeaderAction[] = [
    { key: 'mitre', label: 'MITRE ATT&CK', href: mitreUrl(displayId), external: true },
    { key: 'graph', label: 'Open in Graph', to: graphUrl },
  ];

  const factsStats: FactsStat[] = [];
  if (stats?.doc_count) factsStats.push({ label: 'reports', value: stats.doc_count });
  if (stats?.actor_count) factsStats.push({ label: 'actors', value: stats.actor_count });
  if (stats?.family_count) factsStats.push({ label: 'families', value: stats.family_count });
  if (stats?.cve_count) factsStats.push({ label: 'CVEs', value: stats.cve_count });

  const badges = (
    <>
      {tactics.map((t) => <span key={t} className="explore-tactic-pill">{t}</span>)}
    </>
  );

  const belowValue = (
    <>
      {data?.is_subtechnique && data.parent && (
        <div className="actor-detail-aliases">
          <span className="actor-detail-aliases-label">Sub-technique of:</span>
          <Link to={techniqueDetailPath(data.parent.attack_id)} className="actor-detail-alias-chip">
            {data.parent.attack_id}{data.parent.name ? ` — ${data.parent.name}` : ''}
          </Link>
        </div>
      )}
      {subtechniques.length > 0 && (
        <div className="actor-detail-aliases">
          <span className="actor-detail-aliases-label">Sub-techniques:</span>
          {subtechniques.map((sub) => (
            <Link key={sub.attack_id} to={techniqueDetailPath(sub.attack_id)} className="actor-detail-alias-chip">
              {sub.attack_id}
            </Link>
          ))}
        </div>
      )}
    </>
  );

  return (
    <EntityDetailShell
      header={
        <EntityHeader
          badge="ATT&CK Technique"
          badgeClassName="actor-detail-badge"
          value={
            <>
              <span className="actor-detail-technique-id">{displayId}</span>
              {displayName && <span style={{ marginLeft: 12 }}>{displayName}</span>}
            </>
          }
          copyValue={displayId}
          actions={actions}
          metaStrip={badges}
          belowValue={belowValue}
        />
      }
      facts={
        factsStats.length > 0 ? (
          <FactsBand
            stats={factsStats}
            sparkline={{
              firstSeen: documents.reduce<string | null>((m, d) => (d.date && (!m || d.date < m) ? d.date : m), null),
              lastSeen: documents.reduce<string | null>((m, d) => (d.date && (!m || d.date > m) ? d.date : m), null),
              series: sparklineFromDocs(documents),
            }}
            borderColor={ENTITY_COLORS.technique}
          />
        ) : null
      }
      description={data?.description ? <p className="ioc-detail-description">{data.description}</p> : null}
      notFound={data && !data.found ? (
        <div className="ioc-detail-not-found">
          Technique not indexed in knowledge graph.{' '}
          <a href={mitreUrl(displayId)} target="_blank" rel="noreferrer">View on MITRE ATT&CK</a>
        </div>
      ) : null}
      documents={documents.length > 0 ? (
        <>
          <h3 className="ioc-detail-section-title">Referenced in {documents.length} document{documents.length !== 1 ? 's' : ''}</h3>
          <DocumentsTable documents={documents} />
        </>
      ) : null}
      cooccurrence={coRows.length > 0 ? (
        <>
          <h3 className="ioc-detail-section-title">Used in / Exploited by</h3>
          <CoOccurrenceTable rows={coRows} typeFacetLabel="Type" />
        </>
      ) : null}
    />
  );
}
